import { getApps, initializeApp, type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  initializeAuth,
  type Auth,
} from 'firebase/auth';
import {
  CACHE_SIZE_UNLIMITED,
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';

export type EcoEnv = 'local' | 'staging' | 'production';

export const FUNCTIONS_REGION = 'asia-southeast1';

export const EMULATOR_PORTS = {
  auth: 9099,
  firestore: 8080,
  functions: 5001,
  storage: 9199,
} as const;

export interface InitOptions {
  env: EcoEnv;
  firebaseConfig: FirebaseOptions;
  /** Ops devices keep everything they have read: `unlimited`. Office uses the SDK default. */
  cache?: 'unlimited' | 'default';
  /** Emulator host when env is `local`. */
  emulatorHost?: string;
}

export interface EcoBoxClient {
  env: EcoEnv;
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  functions: Functions;
  storage: FirebaseStorage;
}

const APP_NAME = '[ecobox]';
let client: EcoBoxClient | undefined;

export function isProduction(env: EcoEnv): boolean {
  return env === 'production';
}

export function parseEcoEnv(value: unknown): EcoEnv {
  return value === 'staging' || value === 'production' ? value : 'local';
}

export function initEcoBox(options: InitOptions): EcoBoxClient {
  if (client) return client;
  const { env, firebaseConfig, cache = 'default', emulatorHost = '127.0.0.1' } = options;

  const existing = getApps().find((a) => a.name === APP_NAME);
  const app = existing ?? initializeApp(firebaseConfig, APP_NAME);
  const fresh = existing === undefined;

  const auth = fresh ? initializeAuth(app, { persistence: browserLocalPersistence }) : getAuth(app);
  const db = fresh
    ? initializeFirestore(app, {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
          ...(cache === 'unlimited' ? { cacheSizeBytes: CACHE_SIZE_UNLIMITED } : {}),
        }),
      })
    : getFirestore(app);
  const functions = getFunctions(app, FUNCTIONS_REGION);
  const storage = getStorage(app);

  if (env === 'local' && fresh) {
    connectAuthEmulator(auth, `http://${emulatorHost}:${EMULATOR_PORTS.auth}`, {
      disableWarnings: true,
    });
    connectFirestoreEmulator(db, emulatorHost, EMULATOR_PORTS.firestore);
    connectFunctionsEmulator(functions, emulatorHost, EMULATOR_PORTS.functions);
    connectStorageEmulator(storage, emulatorHost, EMULATOR_PORTS.storage);
  }

  client = { env, app, auth, db, functions, storage };
  return client;
}

/** Default config for the local emulator project. */
export const LOCAL_FIREBASE_CONFIG: FirebaseOptions = {
  apiKey: 'demo-key',
  authDomain: 'demo-ecobox.firebaseapp.com',
  projectId: 'demo-ecobox',
  storageBucket: 'demo-ecobox.appspot.com',
  appId: 'demo-app',
};
